
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import moment from "moment";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "react-datepicker/dist/react-datepicker.css";

import { fetchSupportRequests } from '../../store/actions/Support/SupportActions';
import TableFooter from '../components/Common/TableFooter';
import NoDataFound from '../components/Common/NoDataFound';
import CommonExportButtons from '../components/Common/CommonExportButtons';
import PageSizeSelector from '../components/Common/PageSizeSelector';
import SkeletonTable from '../components/Common/SkeletonTable';

const Support = () => {
  const dispatch = useDispatch();
  const supportData = useSelector(state => state.support.supportRequests) || {};
  const loading = useSelector(state => state.support.loading);
  const supportRows = supportData.rows || [];
  const totalPages = supportData.pages || 1;
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterQuery, setFilterQuery] = useState("");
  const [orderBy, setOrderBy] = useState('');
  const [orderByDir, setOrderByDir] = useState('desc');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });

  const buildSearchQuery = () => {
    const search = searchTerm ? searchTerm : '';
    const params = new URLSearchParams();
    if (search) {
      params.append('searchFields', 'fullName,email,phone,description');
      params.append('search', search);
    }
    if (dateRange.startDate && dateRange.endDate) {
      const startDateStr = dateRange.startDate.format('YYYY/MM/DD');
      const endDateStr = dateRange.endDate.format('YYYY/MM/DD');
      params.append('filter_createdAt', `btw-${startDateStr},${endDateStr}`);
    }
    if (orderBy) {
      params.append('orderBy', orderBy);
      params.append('orderByDir', orderByDir);
    }
    const queryString = `${params.toString()}`;
    return queryString;
  };

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrderByDir(orderByDir === 'desc' ? 'asc' : 'desc');
    } else {
      setOrderBy(field);
      setOrderByDir('desc');
    }
    setCurrentPage(1);
  };

  const onSubmit = async () => {
    const query = buildSearchQuery();
    setFilterQuery(query);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setDateRange({ startDate: null, endDate: null });
    setOrderBy('');
    setOrderByDir('desc');
    setFilterQuery("");
    setCurrentPage(1);
  };

  // Define export headers
  const exportHeaders = [
    { label: 'Name', key: 'fullName' },
    { label: 'Email', key: 'email' },
    { label: 'Phone', key: 'phone' },
    { label: 'Description', key: 'description' },
    { label: 'Created At', key: 'createdAt' }
  ];

  // Define skeleton table columns
  const skeletonColumns = [
    { title: 'Name', width: 120 },
    { title: 'Email', width: 150 },
    { title: 'Phone', width: 120 },
    { title: 'Description', width: 200 },
    { title: 'Created At', width: 140 },
    { title: 'Action', width: 100, height: 32 }
  ];

  useEffect(() => {
    const searchQuery = buildSearchQuery();
    let queryObject = {};
    
    if (searchQuery) {
      // Convert query string to object
      const urlParams = new URLSearchParams(searchQuery);
      urlParams.forEach((value, key) => {
        queryObject[key] = value;
      });
    }
    
    let effectivePageSize = pageSize;
    if (pageSize === 100000) {
      queryObject.noPagination = true;
      effectivePageSize = 100000;
    }
    
    dispatch(fetchSupportRequests(currentPage, effectivePageSize, '', queryObject));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage, pageSize, filterQuery, orderBy, orderByDir]);

  const handleViewDetails = (req) => {
    setSelectedRequest(req);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
  };

  return (
    <>
      <div className='form-head page-titles d-flex align-items-center'>
        <div className='me-auto d-lg-block'>
          <h4 className='mb-1'>Support Requests</h4>
        </div>
      </div>

      {/* Filter Section */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-xl-6 col-sm-6">
                  <label className="form-label">Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name, email, phone, description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-xl-3 col-sm-6">
                  <label className="form-label">Date Range</label>
                  <DateRangePicker
                    initialSettings={{
                      ...(dateRange.startDate && dateRange.endDate && {
                        startDate: dateRange.startDate,
                        endDate: dateRange.endDate,
                      }),
                      locale: {
                        format: 'DD/MM/YYYY',
                      },
                      showDropdowns: true,
                      autoUpdateInput: false,
                    }}
                    onApply={(event, picker) => {
                      setDateRange({
                        startDate: picker.startDate,
                        endDate: picker.endDate,
                      });
                    }}
                    onCancel={() => {
                      setDateRange({ startDate: null, endDate: null });
                    }}
                  >
                    <input
                      type="text"
                      className="form-control input-daterange-timepicker"
                      placeholder="Select date range"
                      readOnly
                      value={
                        dateRange.startDate && dateRange.endDate
                          ? `${dateRange.startDate.format('DD/MM/YYYY')} - ${dateRange.endDate.format('DD/MM/YYYY')}`
                          : ''
                      }
                    />
                  </DateRangePicker>
                </div>
                <div className="col-xl-3 col-sm-6 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-primary me-2"
                    onClick={onSubmit}
                  >
                    <i className="fa fa-search me-1"></i>
                    Search
                  </button>
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={resetFilters}
                    disabled={!(searchTerm || dateRange.startDate || dateRange.endDate || orderBy)}
                  >
                    <i className="fa fa-refresh me-1"></i>
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="d-flex mb-2 align-items-center justify-content-between">
            <PageSizeSelector 
              pageSize={pageSize}
              setPageSize={setPageSize}
              setCurrentPage={setCurrentPage}
              options={[10, 50, 100]}
            />
            <CommonExportButtons 
              data={supportRows} 
              exportHeaders={exportHeaders}
              filename="support-requests"
              pdfTitle="Support Requests List"
              totalText="Total Support Requests"
              pdfOrientation="landscape"
              pdfHeaderColor={[0, 123, 255]}
              includeTimestamp={true}
              dateFields={['createdAt']}
            />
          </div>

          <div className='col-xl-12'>
            <div>
              <div className='table-responsive table-hover fs-14 order-list-table'>
                {loading ? (
                  <SkeletonTable columns={skeletonColumns} rows={8} />
                ) : (!loading && supportRows.length === 0) ? (
                  <NoDataFound 
                    message="No Support Requests Found" 
                    description="No support requests match your current filters." 
                  />
                ) : (
                  <table className='table display table-hover display mb-4 dataTablesCard card-table dataTable no-footer'>
                    <thead>
                      <tr role='row'>
                        <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('fullName')}>
                          Name
                          <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                            <i className="fa fa-caret-up" style={{ color: orderBy === 'fullName' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'fullName' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                            <i className="fa fa-caret-down" style={{ color: orderBy === 'fullName' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'fullName' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                          </span>
                        </th>
                        <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('email')}>
                          Email
                          <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                            <i className="fa fa-caret-up" style={{ color: orderBy === 'email' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'email' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                            <i className="fa fa-caret-down" style={{ color: orderBy === 'email' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'email' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                          </span>
                        </th>
                        <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('phone')}>
                          Phone
                          <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                            <i className="fa fa-caret-up" style={{ color: orderBy === 'phone' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'phone' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                            <i className="fa fa-caret-down" style={{ color: orderBy === 'phone' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'phone' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                          </span>
                        </th>
                        <th>Description</th>
                        <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('createdAt')}>
                          Created At
                          <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                            <i className="fa fa-caret-up" style={{ color: orderBy === 'createdAt' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'createdAt' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                            <i className="fa fa-caret-down" style={{ color: orderBy === 'createdAt' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'createdAt' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                          </span>
                        </th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supportRows.map((req) => (
                        <tr key={req._id}>
                          <td>{req.fullName}</td>
                          <td>{req.email}</td>
                          <td>{req.phone}</td>
                          <td>
                            <div style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={req.description}>
                              {req.description}
                            </div>
                          </td>
                          <td>{moment(req.createdAt).format("DD/MM/YYYY, hh:mm A")}</td>
                          <td>
                            <div className="d-flex">
                              <button className="btn btn-info btn-sm shadow" onClick={() => handleViewDetails(req)}>
                                <i className="fa fa-eye me-1"></i>
                                View Details
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {(!loading && supportRows.length > 0) && (
        <TableFooter
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalEntries={supportData?.total || supportRows.length}
          onPageChange={setCurrentPage}
        />
      )}
        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Support Request Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedRequest && (
              <>
                <p><strong>Name:</strong> {selectedRequest.fullName}</p>
                <p><strong>Email:</strong> {selectedRequest.email}</p>
                <p><strong>Phone:</strong> {selectedRequest.phone}</p>
                <p><strong>Description:</strong> {selectedRequest.description}</p>
                <p><strong>Created At:</strong> {moment(selectedRequest.createdAt).format("DD/MM/YYYY, hh:mm A")}</p>
              </>
            )}
          </Modal.Body>
        </Modal>
      </>
    );
  };

  export default Support;
